import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { dateFmt, relative, PRIORITY_DOT } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ListTodo, Clock, Bell, CalendarClock, CheckSquare } from "lucide-react";

export default function MyWork() {
  const { me } = useApp();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["my-work", me?.id], queryFn: () => get(`/my-work?user_id=${me.id}`), enabled: !!me });

  if (isLoading || !data) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="My Work" subtitle="Everything assigned to you, across every module." />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <KpiCard testid="mywork-open" variant="hero" accent="blue" label="Open Items" value={data.open_items} sub="assigned to you" icon={ListTodo} />
        <KpiCard testid="mywork-overdue" accent="red" label="Overdue" value={data.overdue} sub="past due date" icon={Clock} />
        <KpiCard testid="mywork-notifs" accent="violet" label="Unread Notifications" value={data.notifications.length} sub="need attention" icon={Bell} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="My Assigned Tickets" testid="mywork-items">
          {data.items.length === 0 ? <EmptyState icon={CheckSquare} title="Nothing assigned to you" /> : (
            <div className="divide-y divide-border">
              {data.items.map((it) => (
                <button key={it.id} onClick={() => navigate(`/pm/projects/${it.project_id}/items/${it.id}`)}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/50">
                  <span className={cn("h-2 w-2 rounded-full", PRIORITY_DOT[it.priority])} />
                  <span className="flex-1 text-sm">{it.title}</span>
                  {it.due_date && <span className="text-xs text-muted-foreground">{dateFmt(it.due_date)}</span>}
                </button>
              ))}
            </div>
          )}
        </Section>

        <div className="space-y-6">
          <Section title="Upcoming Deadlines" testid="mywork-deadlines">
            {data.upcoming_deadlines.length === 0 ? <EmptyState icon={CalendarClock} title="No upcoming deadlines" /> : (
              <div className="divide-y divide-border">
                {data.upcoming_deadlines.map((it) => (
                  <button key={it.id} onClick={() => navigate(`/pm/projects/${it.project_id}/items/${it.id}`)}
                    className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-muted/50">
                    <span className="text-sm">{it.title}</span>
                    <span className="text-xs text-muted-foreground">{dateFmt(it.due_date)}</span>
                  </button>
                ))}
              </div>
            )}
          </Section>

          <Section title="Notifications" testid="mywork-notifications">
            {data.notifications.length === 0 ? <EmptyState icon={Bell} title="All caught up" /> : (
              <div className="divide-y divide-border">
                {data.notifications.map((n) => (
                  <button key={n.id} onClick={() => n.link && navigate(n.link)}
                    className="flex w-full items-start gap-2 px-4 py-2.5 text-left hover:bg-muted/50">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <div>
                      <p className="text-sm">{n.message}</p>
                      <p className="text-xs text-muted-foreground">{relative(n.created_at)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
